<template>
  <div>
    <section v-if="accounts.length === 0"
      class="bg-white dark:bg-gray-900">
      <div class="py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6">
        <div class="mx-auto max-w-screen-sm text-center">
          <p class="mb-4 text-3xl tracking-tight font-bold text-gray-900 md:text-4xl dark:text-white">
            No Telephony Account
          </p>
          <p class="mb-4 text-lg font-light text-gray-500 dark:text-gray-400">
            Sorry, you don't have any telephony account configured yet. Please configure a new Twilio or SignalWire
            account
            to add phone numbers.
          </p>
          <a href="/telephony_accounts/new"
            class="inline-flex text-white bg-primary-600 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:focus:ring-primary-900 my-4">
            Configure Telephony Account
          </a>
        </div>
      </div>
    </section>

    <section v-else>
      <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div class="col-span-4">
          <div>
            <fwb-select
              v-model="account"
              :options="accounts"
              @change="fetchPhoneNumbers"
              label="Telephony Account" />
          </div>

          <div v-if="!isSignalwireAccount()"
            class="mt-4">
            <label for="area-code" class="block text-sm font-medium text-gray-700 z-50">
              Country
            </label>

            <multi-select
              v-model="country"
              :options="countryList"
              placeholder="Select Country"
              :custom-label="customCountryLabel"
              class="min-w-max placeholder-gray-300 text-sm mt-2 bg-gray-50"
              input-container-class="bg-gray-50"
              :allow-empty="false"
              deselect-label=""
              @select="fetchPhoneNumbers">
              <template #singleLabel="itemProps">
                {{ itemProps.option.name }}
              </template>
              <template #option="itemProps">
                {{ itemProps.option.name }}
              </template>
            </multi-select>
          </div>

          <div class="mt-4">
            <fwb-input
              v-model="areaCode"
              placeholder="Area code"
              label="Area Code"
              @change="fetchPhoneNumbers" />
          </div>
        </div>

        <div class="col-span-6 relative">
          <splash-spinner v-if="phoneNumbersLoading" />

          <div class="rounded-md border border-gray-200 shadow-sm relative">
            <table class="w-full text-sm text-left text-gray-500">
              <caption class="p-5 text-lg font-semibold text-left text-gray-900 bg-white rounded-md">
                Available Phone Numbers
                <p class="mt-1 text-sm font-normal text-gray-500 dark:text-gray-400">
                  Please select country and area code to look at available phone numbers.
                </p>
              </caption>

              <tbody class="bg-grey-light flex flex-col justify-between overflow-y-scroll w-full h-96 bg-gray-50">
                <tr v-if="phoneNumberList.length === 0" class="flex-grow">
                  <td colspan="2" class="flex justify-center items-center h-full text-center py-4">
                    <svg
                      class="w-5 h-5 mr-1 text-gray-500 dark:text-white" aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path
                        stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>

                    No phone numbers found for your criteria
                  </td>
                </tr>

                <tr
                  v-for="(phoneNumber) in phoneNumberList"
                  :key="phoneNumber.number"
                  class="border-b cursor-pointer"
                  :class="{
                    'bg-blue-200': phoneNumber.number === selectedPhoneNumber,
                    'even:bg-white even:dark:bg-gray-900 odd:bg-gray-50 odd:dark:bg-gray-800': phoneNumber.number !== selectedPhoneNumber
                  }"
                  @click="selectNumber(phoneNumber.number)">
                  <th class="px-6 py-2 font-medium text-gray-900 whitespace-nowrap w-40">
                    {{ phoneNumber.number }}
                  </th>

                  <td class="px-6 py-2">
                    {{ locality(phoneNumber) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="actions mt-10 border-t border-gray-200 pt-5">
        <submit-spinner
          title="Add Phone Number"
          :form-submitting="formSubmitting"
          @click="addSelectedPhoneNumber" />

        <a
          href="/phone_numbers"
          class="text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700">
          Cancel
        </a>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import MultiSelect from 'vue-multiselect'
import { FwbInput, FwbSelect } from 'flowbite-vue'
import securedAxios from '../../utils/axios'
import { toastSuccess, toastWarn, toastError } from '~/javascript/utils/toast'
import SplashSpinner from '../common/splash_spinner.vue'
import SubmitSpinner from '../common/submit_spinner.vue'

const DEFAULT_COUNTRY = 'US'

const props = defineProps({
  accounts: {
    type: Array,
    required: true
  }
})

const axios = securedAxios()

const accounts = props.accounts.map((acc) => ({ value: acc.id, name: acc.name }))
const account = ref(accounts[0]?.value)
const countryList = ref([])
const country = ref('')
const areaCode = ref('')
const phoneNumberList = ref([])
const selectedPhoneNumber = ref('')

const phoneNumbersLoading = ref(false)
const formSubmitting = ref(false)

function isSignalwireAccount() {
  return props.accounts.find((acc) => acc.id === account.value)?.provider === 'signalwire'
}

async function fetchCountries() {
  try {
    const response = await axios.get('/ajax/phone_numbers/countries')
    countryList.value = response.data
  } catch (error) {
    toastError(error)
  }
}

async function fetchPhoneNumbers() {
  selectedPhoneNumber.value = ''
  phoneNumberList.value = []

  let countryCode = DEFAULT_COUNTRY
  if (!isSignalwireAccount()) {
    countryCode = country.value.alpha2
  }

  // To prevent undesired requests on start
  if (!countryCode) {
    return
  }

  try {
    phoneNumbersLoading.value = true
    const url = `/ajax/phone_numbers/search?country=${countryCode}&area_code=${encodeURIComponent(areaCode.value)}&account=${account.value}`
    const response = await axios.get(url)
    phoneNumberList.value = response.data
  } catch (error) {
    toastError(error)
  } finally {
    phoneNumbersLoading.value = false
  }
}

async function addSelectedPhoneNumber() {
  if (!selectedPhoneNumber.value) {
    toastWarn('No phone number selected')
    return
  }

  formSubmitting.value = true
  try {
    const params = {
      number: selectedPhoneNumber.value,
      account: account.value
    }

    await axios.post('/ajax/phone_numbers/', params)
    toastSuccess('Phone number added successfully!')

    setTimeout(() => {
      window.Turbo.visit('/phone_numbers')
    }, 1500)
  } catch (error) {
    toastError(error)
  } finally {
    formSubmitting.value = false
  }
}

function customCountryLabel({ name, alpha2 }) {
  return `${name} – ${alpha2}`
}

function locality(phone) {
  return phone.locality ? `${phone.locality}, ${phone.region}` : phone.region
}

function selectNumber(phoneNumber) {
  selectedPhoneNumber.value = phoneNumber
}

onMounted(async() => {
  await fetchCountries()
})

</script>

<style>
.multiselect__input, .multiselect__tags, .multiselect__single {
  border-color: rgb(209 213 219) !important;
  background-color: rgb(249 250 251) !important;
  font-size: 14px !important;
}

.multiselect__option--highlight, .multiselect__option--highlight:after {
  background: rgb(37 99 235) !important;
}
</style>
